import React, {useContext, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useSearchParams} from 'react-router-dom';
import {AuthContext} from '../auth/ContextAuthentication';
import {ADMIN_CAPABILITIES} from '../auth/adminCapabilities';
import PremiumOperationsConsole from './PremiumOperations.jsx';
import PremiumReports from './PremiumReports.jsx';

const Metric = ({label, value}) => (
  <div className="rounded bg-white p-4 shadow">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-1 text-xl font-semibold">{value}</p>
  </div>
);

const percentage = (numerator, denominator) => denominator
  ? `${((numerator / denominator) * 100).toFixed(2)}%`
  : '0%';

const PremiumAnalyticsPanel = () => {
  const {accessToken} = useContext(AuthContext);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState('');

  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {Authorization: `Bearer ${accessToken}`},
    timeout: 15000,
  }), [accessToken]);

  useEffect(() => {
    let active = true;
    api.get('api/premium/admin/analytics?days=30')
      .then((response) => { if (active) setDashboard(response.data.dashboard); })
      .catch((requestError) => { if (active) setError(requestError?.response?.data?.message || 'Premium funnel analytics are unavailable.'); });
    return () => { active = false; };
  }, [api]);

  const eventCount = (event) => dashboard?.events?.find((item) => item.event === event)?.count || 0;
  const paywallViews = eventCount('paywall_viewed');
  const selections = eventCount('plan_selected');
  const verified = eventCount('payment_completed');
  const failed = eventCount('payment_failed');

  return (
    <section className="mb-5 space-y-4">
      <div className="rounded bg-white p-5 shadow">
        <h2 className="text-lg font-semibold">Premium conversion — last 30 days</h2>
        <p className="mt-1 text-sm text-gray-600">PII-safe client discovery events and backend-verified payment outcomes.</p>
        {error && <p className="mt-3 rounded bg-yellow-50 p-3 text-sm text-yellow-800">{error}</p>}
      </div>
      {dashboard && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Metric label="Paywall views" value={paywallViews} />
            <Metric label="Plan selections" value={selections} />
            <Metric label="Verified activations" value={verified} />
            <Metric label="Failed payments" value={failed} />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Metric label="View → selection" value={percentage(selections, paywallViews)} />
            <Metric label="Selection → activation" value={percentage(verified, selections)} />
            <Metric label="Verified payment success" value={percentage(verified, verified + failed)} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded bg-white p-5 shadow">
              <h3 className="font-semibold">Paywall sources</h3>
              <div className="mt-3 space-y-2 text-sm">
                {(dashboard.sources || []).length === 0 && <p className="text-gray-500">No source events yet.</p>}
                {(dashboard.sources || []).map((item) => <div key={item.source} className="flex justify-between border-b py-2"><span>{item.source}</span><strong>{item.count}</strong></div>)}
              </div>
            </div>
            <div className="rounded bg-white p-5 shadow">
              <h3 className="font-semibold">Plan funnel</h3>
              <div className="mt-3 space-y-2 text-sm">
                {(dashboard.plans || []).length === 0 && <p className="text-gray-500">No plan events yet.</p>}
                {(dashboard.plans || []).map((item, index) => <div key={`${item.planCode}-${item.event}-${index}`} className="flex justify-between border-b py-2"><span>{item.planCode} · {item.event}</span><strong>{item.count}</strong></div>)}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default function PremiumOperations() {
  const {adminSession, hasCapability} = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const superAdmin = adminSession?.role === 'Super Admin';
  const canOperate = superAdmin || hasCapability(ADMIN_CAPABILITIES.PREMIUM_READ);
  const canReport = superAdmin || hasCapability(ADMIN_CAPABILITIES.PREMIUM_REPORTS_READ);
  const view = canReport && (searchParams.get('premiumView') === 'reports' || !canOperate) ? 'reports' : 'operations';
  const setView = (nextView) => {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'reports') next.set('premiumView', 'reports'); else next.delete('premiumView');
    setSearchParams(next);
  };

  return (
    <>
      <div className="mb-5 rounded bg-white p-4 shadow">
        <div className="flex flex-wrap gap-2">
          {canOperate && <button onClick={() => setView('operations')} className={`rounded px-4 py-2 ${view === 'operations' ? 'bg-[#0C7240] text-white' : 'bg-gray-100'}`}>Premium operations</button>}
          {canReport && <button onClick={() => setView('reports')} className={`rounded px-4 py-2 ${view === 'reports' ? 'bg-[#0C7240] text-white' : 'bg-gray-100'}`}>Reports</button>}
        </div>
      </div>
      {view === 'reports' ? <PremiumReports /> : <><PremiumAnalyticsPanel /><PremiumOperationsConsole /></>}
    </>
  );
}
